import { t } from '../../../../i18n/i18n';

/**
 * Super Affiliate promo-code rules, as live has them (`PromoCodeSetup.jsx` and
 * `affiliateReducer.js` on main). The dashboard's `super_affiliate` block can
 * send its own `promo_code_rules`; these are what applies when it doesn't.
 */

/** Our rule name → the `code_type` the promo-code endpoints take. */
export const CODE_TYPES = {
  one_time_50: 'super_one_time_50',
  normal: 'super_normal',
};

export const APPLICATION_STATUS = {
  underReview: 'under review',
  approved: 'approved',
  rejected: 'rejected',
};

export const CODE_LABELS = {
  one_time_50: 'One-Time 50% Code',
  normal: 'Normal Promotion Code',
};

export const CODE_HINTS = {
  one_time_50: 'Available once only. Must end in 50 — the rest is your choice (e.g. LK50).',
  normal: 'Your everyday code. During a sale it matches the best available offer.',
};

const MIN_CODE_LENGTH = 2;

const DEFAULT_RULES = {
  one_time_50: {
    discount_percent: 50,
    commission_percent: 10,
    required_suffix: '50',
    min_length: 2,
    max_length: 16,
    example: 'LK50',
  },
  normal: {
    discount_percent: 10,
    commission_percent: 10,
    required_suffix: null,
    min_length: 2,
    max_length: 16,
    example: 'LK',
  },
};

/**
 * A claimed code comes back as an object — `{ id, code, code_type,
 * discount_percent, commission_percent, … }` — or, from older rows, the bare
 * string. Rendering the object itself is React error #31.
 */
export const codeValue = (entry) => (typeof entry === 'string' ? entry : entry?.code || '');

/** `promo_codes` is keyed by rule name, but an array keyed by `code_type` is tolerated. */
export const promoCodeEntry = (superAffiliate, key) => {
  const codes = superAffiliate?.promo_codes;
  if (!codes) return null;
  if (Array.isArray(codes)) return codes.find((entry) => entry?.code_type === CODE_TYPES[key]) || null;
  return codes[key] ?? null;
};

/** A claimed code carries its own terms; otherwise the advertised ones. */
export const codeTerms = (entry, rules) => ({
  discount: entry?.discount_percent ?? rules?.discount_percent,
  commission: entry?.commission_percent ?? rules?.commission_percent,
});

/**
 * The server's rules win on the terms, but not on the minimum length (floored
 * at two) or the example, which stays our copy.
 */
export const rulesFor = (superAffiliate, key) => {
  const defaults = DEFAULT_RULES[key];
  return {
    ...defaults,
    ...superAffiliate?.promo_code_rules?.[key],
    min_length: MIN_CODE_LENGTH,
    example: defaults?.example || 'LK',
  };
};

export const codeKeysFor = (superAffiliate) => {
  const rules = superAffiliate?.promo_code_rules;
  return rules && Object.keys(rules).length ? Object.keys(rules) : Object.keys(DEFAULT_RULES);
};

/** The backend's rules, checked here first so an obvious mistake costs no request. */
export const validateCode = (code, rules) => {
  const value = String(code || '').trim();
  if (!value) return t('Enter a code');
  if (!/^[A-Z0-9]+$/.test(value)) return t('Use letters and numbers only, no spaces');
  if (rules?.min_length && value.length < rules.min_length) return t('Minimum {{count}} characters', { count: rules.min_length });
  if (rules?.max_length && value.length > rules.max_length) return t('Maximum {{count}} characters', { count: rules.max_length });
  if (rules?.required_suffix && !value.endsWith(rules.required_suffix)) return t('Must end with {{suffix}}', { suffix: rules.required_suffix });
  return null;
};

/** One readable line from an RTK Query error — `detail`, `message` or `error`. */
export const apiMessage = (err, fallback) =>
  err?.data?.detail || err?.data?.message || err?.data?.error || fallback;
